import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  content,
  getLocalizedBlogPostBySlug,
  BlogPost,
  SupportedLocale,
  localizeBlogPost,
} from '../lib/content';

type BlogArticlePageProps = {
  locale?: SupportedLocale;
  slug: string;
};

const buildRelated = (allPosts: BlogPost[], current: BlogPost): BlogPost[] => {
  // 1) Ręcznie powiązane (jeśli są), ale TYLKO z tej samej kategorii
  const fromRelated =
    current.relatedSlugs
      ?.map((slug) => allPosts.find((p) => p.slug === slug))
      .filter((p): p is BlogPost => Boolean(p))
      .filter((p) => p.category === current.category && p.slug !== current.slug) ?? [];

  // 2) Pozostałe artykuły z tej samej kategorii (bez current)
  const inCategory = allPosts.filter(
    (post) => post.category === current.category && post.slug !== current.slug
  );

  // 3) Połącz i usuń duplikaty
  const combined = [...fromRelated, ...inCategory];
  const unique: BlogPost[] = [];

  combined.forEach((post) => {
    if (!unique.some((p) => p.slug === post.slug)) unique.push(post);
  });

  // 4) Max 3 artykuły
  return unique.slice(0, 3);
};


export const BlogArticlePage: React.FC<BlogArticlePageProps> = ({
  locale = 'pl',
  slug,
}) => {
  const allPosts = content.pl.blog.posts;
  const basePost = useMemo(
    () => getLocalizedBlogPostBySlug(slug, locale) ?? localizeBlogPost(allPosts[0], locale),
    [allPosts, slug, locale]
  );
  const related = useMemo(
    () => buildRelated(allPosts, basePost),
    [allPosts, basePost]
  );

  const post = basePost;
  const isParagraf = post.isParagrafZwierzoochronny;
  const articleClasses = `blog-article${isParagraf ? ' article--paragraf' : ''}`;

  const paragrafLabelByLocale: Record<SupportedLocale, string> = {
    pl: 'Paragraf Zwierzoochronny',
    en: 'Zooprotection Paragraph',
    es: 'Párrafo Zooprotector',
  };

  const labelsByLocale: Record<
    SupportedLocale,
    {
      breadcrumbHome: string;
      breadcrumbBlog: string;
      relatedTitle: string;
      readLabel: string;
      defaultCtaTitle: string;
      defaultCtaText: string;
      ctaButton: string;
    }
  > = {
    pl: {
      breadcrumbHome: 'Strona główna',
      breadcrumbBlog: 'Blog',
      relatedTitle: 'Powiązane artykuły',
      readLabel: 'Czytaj',
      defaultCtaTitle: 'Potrzebujesz pomocy w podobnej sprawie?',
      defaultCtaText:
        'Skontaktuj się, aby omówić Twoją sytuację i dobrać najlepsze możliwe działania. Pracuję w języku polskim, angielskim i hiszpańskim.',
      ctaButton: 'Umów konsultację',
    },
    en: {
      breadcrumbHome: 'Home',
      breadcrumbBlog: 'Blog',
      relatedTitle: 'Related articles',
      readLabel: 'Read',
      defaultCtaTitle: 'Need help with a similar matter?',
      defaultCtaText:
        'Get in touch to discuss your situation and choose the best possible course of action. I work in Polish, English and Spanish.',
      ctaButton: 'Book a consultation',
    },
    es: {
      breadcrumbHome: 'Inicio',
      breadcrumbBlog: 'Blog',
      relatedTitle: 'Artículos relacionados',
      readLabel: 'Leer',
      defaultCtaTitle: '¿Necesitas ayuda en un asunto similar?',
      defaultCtaText:
        'Ponte en contacto para comentar tu situación y elegir la mejor estrategia posible. Trabajo en polaco, inglés y español.',
      ctaButton: 'Concertar una consulta',
    },
  };

  const labels = labelsByLocale[locale];
  const categoryLabels: Record<SupportedLocale, Record<string, string>> = {
    pl: {
      'Prawo ochrony zwierząt': 'Prawo ochrony zwierząt',
      'Poradnik klienta': 'Poradnik klienta',
      'Prawo imigracyjne': 'Prawo imigracyjne',
      'Finanse i kredyty': 'Finanse i kredyty',
    },
    en: {
      'Prawo ochrony zwierząt': 'Animal protection law',
      'Poradnik klienta': 'Client guide',
      'Prawo imigracyjne': 'Immigration law',
      'Finanse i kredyty': 'Finance and loans',
    },
    es: {
      'Prawo ochrony zwierząt': 'Derecho de protección de los animales',
      'Poradnik klienta': 'Guía para clientes',
      'Prawo imigracyjne': 'Derecho de inmigración',
      'Finanse i kredyty': 'Finanzas y créditos',
    },
  };

  const monthMap: Record<string, number> = {
    stycznia: 0,
    lutego: 1,
    marca: 2,
    kwietnia: 3,
    maja: 4,
    czerwca: 5,
    lipca: 6,
    sierpnia: 7,
    września: 8,
    października: 9,
    listopada: 10,
    grudnia: 11,
  };

  const monthNamesEn = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const monthNamesEs = [
    'enero',
    'febrero',
    'marzo',
    'abril',
    'mayo',
    'junio',
    'julio',
    'agosto',
    'septiembre',
    'octubre',
    'noviembre',
    'diciembre',
  ];

  const formatDate = (dateStr: string): string => {
    if (locale === 'pl') return dateStr;
    const parts = dateStr.split(' ').filter(Boolean);
    if (parts.length < 3) return dateStr;
    const [dayStr, monthStr, yearStr] = parts;
    const day = parseInt(dayStr, 10);
    const monthIndex = monthMap[monthStr.toLowerCase()];
    const year = parseInt(yearStr, 10);
    if (
      Number.isNaN(day) ||
      Number.isNaN(year) ||
      monthIndex === undefined ||
      monthIndex < 0 ||
      monthIndex > 11
    ) {
      return dateStr;
    }
    if (locale === 'en') {
      return `${day} ${monthNamesEn[monthIndex]} ${year}`;
    }
    // es
    return `${day} de ${monthNamesEs[monthIndex]} de ${year}`;
  };

  const formatReadingTime = (readingTime: string): string => {
    if (locale === 'pl') return readingTime;
    const match = readingTime.match(/(\d+)/);
    const minutes = match ? match[1] : '';
    if (!minutes) {
      return locale === 'en' ? 'Few minutes read' : 'Unos minutos de lectura';
    }
    if (locale === 'en') {
      return `${minutes} min read`;
    }
    // es
    return `${minutes} min de lectura`;
  };
  // CTA: "Sprawdź, jak pomagamy" — mapowanie artykuł → landing/hub
  const getHelpHrefForPost = (post: BlogPost): string => {
    const bySlug: Record<string, string> = {
      // Zwierzęta — linkuj do konkretnych landingów
      'odszkodowanie-za-pogryzione-zwierze': '/zwierzeta/cywil/pogryzienie-zwierzecia',
      'zadoscuczynienie-za-pogryzione-zwierze': '/zwierzeta/cywil/pogryzienie-zwierzecia',
      'rekojmia-za-wady-zwierzecia': '/zwierzeta/cywil/rekojmia-zwierze',
      'umowa-adopcyjna': '/zwierzeta/cywil/spor-o-psa-po-rozstaniu',
      'czy-jestem-wlascicielem': '/zwierzeta/cywil/spor-o-psa-po-rozstaniu',
      'wlasnosc-znalezionego-zwierzecia': '/zwierzeta/cywil/spor-o-psa-po-rozstaniu',
'znecanie-sie-nad-zwierzetami': '/blog#paragraf-zwierzoochronny',

      // Banki — jeśli masz takie slugi (podmień, jeśli u Ciebie są inne)
      'kredyty-frankowe-sytuacja-2026': '/spory-kredytobiorcow-z-bankami/franki',
    };
    if (bySlug[post.slug]) return bySlug[post.slug];

    // Fallback: jeśli to "Paragraf Zwierzoochronny" → hub zwierzęcy
    if (post.isParagrafZwierzoochronny) return '/prawa-zwierzat/cywil';

    // Fallback: jeśli to finanse/kredyty → hub bankowy
    if (post.category === 'Finanse i kredyty') return '/spory-kredytobiorcow-z-bankami';

    // Inne przypadki → blog
    return '/blog';
  };

const helpHref = getHelpHrefForPost(post);

  const mediaButtonsByLocale: Record<
    SupportedLocale,
    {
      programme: string;
      katowice: string;
      sourcesHeading: string;
    }
  > = {
    pl: {
      programme: 'Obejrzyj program z moim udziałem',
      katowice: 'Zobacz nagranie z Katowic',
      sourcesHeading: 'Źródła i materiały',
    },
    en: {
      programme: 'Watch the programme featuring me',
      katowice: 'Watch the recording from Katowice',
      sourcesHeading: 'Sources and further reading',
    },
    es: {
      programme: 'Ver el programa con mi participación',
      katowice: 'Ver la grabación de Katowice',
      sourcesHeading: 'Fuentes y materiales',
    },
  };

  const mediaLabels = mediaButtonsByLocale[locale];

  const programmeHref =
    'https://tvn24.pl/plus/programy/tak-jest/czy-mozna-bronic-sie-przed-atakiem-psa-eksperci-tlumacza-przepisy-i-zasady-vc9154182';

  const katowiceHref =
    'https://katowice24.info/artykul/pies-bez-smyczy-zaatakowal-n2405097';

  return (
  <>
    <div className={articleClasses}>
      <div className="container">
        <nav className="breadcrumb" aria-label="Ścieżka nawigacji">
          <a href="/">{labels.breadcrumbHome}</a>
          <span aria-hidden="true">›</span>
          <a href="/blog">{labels.breadcrumbBlog}</a>
          <span aria-hidden="true">›</span>
          <span className="muted" aria-current="page">
            {post.title}
          </span>
        </nav>

        <div className="article-header">
          <span className={`badge${isParagraf ? ' badge--paragraf' : ''}`}>
            {isParagraf
              ? paragrafLabelByLocale[locale]
              : categoryLabels[locale][post.category] ?? post.category}
          </span>
          <h1>{post.title}</h1>
          <div className="article-meta">
            {isParagraf && (
              <>
                <span>{categoryLabels[locale][post.category] ?? post.category}</span>
                <span aria-hidden="true">•</span>
              </>
            )}
            <span>{formatDate(post.date)}</span>
            <span aria-hidden="true">•</span>
            <span>{formatReadingTime(post.readingTime)}</span>
          </div>
        </div>

        <article className="article-body">
                   {post.sections?.map((section, sectionIndex) => {
            const showMediaButtons =
              post.slug === 'psia-debata-tvn24' &&
              section.heading === mediaLabels.sourcesHeading;

            return (
              <React.Fragment key={`${section.heading}-${sectionIndex}`}>
                {showMediaButtons && (
                  <div
                    style={{
                      display: 'flex',
                      gap: 12,
                      flexWrap: 'wrap',
                      marginTop: 24,
                      marginBottom: 28,
                    }}
                  >
                    <a
                      className="btn btn-primary"
                      href={programmeHref}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        background: '#1E3A5F',
                        borderColor: '#1E3A5F',
                        color: '#FFFFFF',
                      }}
                    >
                      {mediaLabels.programme}
                    </a>

                    <a
                      className="btn btn-primary"
                      href={katowiceHref}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        background: '#1E3A5F',
                        borderColor: '#1E3A5F',
                        color: '#FFFFFF',
                      }}
                    >
                      {mediaLabels.katowice}
                    </a>
                  </div>
                )}

                <section className="article-section">
                  {/* Featured Image for PZ articles - floated with accent, within first section so text wraps around */}
                  {isParagraf && post.featuredImage && sectionIndex === 0 && (
                    <figure className="article-featured-inline">
                      <div className="article-featured-accent" aria-hidden="true" />
                      <img
                        src={post.featuredImage.src}
                        alt={post.featuredImage.alt}
                        loading="eager"
                      />
                    </figure>
                  )}

                  {section.heading && <h2>{section.heading}</h2>}

                  {section.paragraphs.map((para, paraIndex) => (
                    <div
                      key={`${sectionIndex}-p-${paraIndex}`}
                      className="markdown-content"
                    >
                      <ReactMarkdown>{para}</ReactMarkdown>
                    </div>
                  ))}

                  {section.images &&
                    section.images.map((image, imageIndex) => (
                      <figure
                        key={`${sectionIndex}-img-${imageIndex}`}
                        className="article-image"
                      >
                        <img src={image.src} alt={image.alt} loading="lazy" />
                        {image.caption && (
                          <figcaption>{image.caption}</figcaption>
                        )}
                      </figure>
                    ))}

                  {section.list && (
                    <ul
                      className={
                        section.listStyle === 'paw' ? 'list--paw' : undefined
                      }
                    >
                      {section.list.map((item, itemIndex) => (
                        <li key={`${sectionIndex}-li-${itemIndex}`}>
                          <div className="markdown-content">
                            <ReactMarkdown>{item}</ReactMarkdown>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              </React.Fragment>
            );
          })}
        </article>

        <div className="article-cta card">
          <h3>{post.ctaTitle ?? labels.defaultCtaTitle}</h3>
          <p>
            {post.ctaText ?? labels.defaultCtaText}
          </p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
  <a className="btn btn-primary" href="/#kontakt">
    {labels.ctaButton}
  </a>

  <a className="btn btn-primary" href={helpHref}>
    {locale === 'en'
      ? 'See how we help'
      : locale === 'es'
      ? 'Ver cómo ayudamos'
      : 'Sprawdź, jak pomagamy'}
  </a>
</div>
        </div>

      {related.length > 0 && (
  <section className="related-articles">
    <h3>{labels.relatedTitle}</h3>
    <div className="blog-tiles">
      {related.map((item) => (
        <article className="card blog-card" key={item.slug}>
          <div className="blog-meta">
            <span className="badge">
              {categoryLabels[locale][item.category] ?? item.category}
            </span>
            <span className="muted blog-meta-detail">
              {formatDate(item.date)} • {formatReadingTime(item.readingTime)}
            </span>
          </div>
          <h4>{localizeBlogPost(item, locale).title}</h4>
          <p>{localizeBlogPost(item, locale).teaser}</p>
          <a className="inline-link with-icon" href={item.link}>
            {labels.readLabel}
            <span aria-hidden="true">→</span>
          </a>
        </article>
      ))}
    </div>
  </section>
)}

      </div>
        </div>
  </>
);
};
